import type {
    ExecutionControlParameters,
    WorkflowExecutionRequestHeaders
} from '../types/workflow-execution';
import type { Excel } from './excel';
import type { M3Api } from './m3';
import { type EnrichedM3Filter, type M3Filter } from './m3-filter';
import { type GenericCode } from './generic-code';
import { type TransformerMorph } from './transformer-morph';
import type { ScheduleTrigger, EventTrigger, ClientTrigger, TriggerType, FormField, FormGroup, LookupConfig } from './trigger';
import type { Email } from './email';
import type { Converter } from './converter';
import type { Sms } from './sms';
import type { FileUploader } from './file-uploader';
import type { TableUpdater } from './table-updater';
import type { Table } from './table';
import type { EnrichedGenericFilter, GenericFilter } from './generic-filter';
import type { APIListResponse } from 'vince-core/types/api';
import type { User } from 'vince-core/types/users';
import { DataType } from 'vince-core/types/common';

export const enum TemplateGroup {
    SINGLE = 'single',
    MULTI = 'multi'
}

export const enum StepType {
    TRIGGER = 'TRIGGER',
    API = 'API',
    M3_FETCH = 'M3_FETCH',
    EXCEL = 'EXCEL',
    TABLE = 'TABLE',
    FILTER = 'FILTER',
    TRANSFORMER_MORPH = 'TRANSFORMER_MORPH',
    EMAIL = 'EMAIL',
    SMS = 'SMS',
    GENERIC_API = 'GENERIC_API',
    GENERIC_CODE = 'GENERIC_CODE',
    DATA_LAKE = 'DATA_LAKE',
    TABLE_UPDATER = 'TABLE_UPDATER',
    GENERIC_FILTER = 'GENERIC_FILTER',
    FILE_LOADER = 'FILE_LOADER',
    CONVERTER = 'CONVERTER',
}

export interface Step {
    stepId: string;
    name: string;
    type: StepType;
    target: string;
    retryAttempts?: number;
    childSteps?: Step[];
}

export interface Overview {
    steps: Step[];
    triggerType: TriggerType;
}

export interface Stats {
    numRunsFailed: number;
    numRuns: number;
    numRunsSuccessful: number;
    lastExecutionTime?: string;
}

export interface WorkflowTags {
    connectionId: string[];
    apiEnvironment?: string;
    environments: string[];
    primaryEnvironment: string;
    tableName?: string;
}

export interface WorkflowEnvironmentDefinition {
    environmentId?: string;
    connections: Record<string, string>;
    variables?: Record<string, string>;
}

export interface WorkflowRuntimeConfig {
    defaultEnvironment?: string;
    connectionAliases?: string[];
    environments?: Record<string, WorkflowEnvironmentDefinition>;
}

export enum WorkflowType {
    EXPRESS = 'EXPRESS',
    STANDARD = 'STANDARD',
}

export type StepDefinition = ScheduleTrigger | EventTrigger | ClientTrigger | GenericCode | TransformerMorph | M3Api |
    Excel | M3Filter | EnrichedM3Filter | Email | Converter | Sms | FileUploader | TableUpdater | Table | GenericFilter
    | EnrichedGenericFilter;

export type StepsConfigMap = Record<string, StepDefinition>;

export enum AdditionalWorkflowInputDataType {
    BOOLEAN = 'BOOLEAN',
    NUMBER = 'NUMBER',
}

export type InputDataType = DataType | AdditionalWorkflowInputDataType;


export interface WorkflowInputConfig {
    dataType: InputDataType;
    validation: {
        required: boolean;
    }
}

export interface ClientConfig {
    buttonText: string;
    description: string;
    title: string;
    fields: FormField[];
    filter?: FormField[], // Was existing on some workflow but is it real field?
    formGroups: FormGroup[];
    lookupConfig?: LookupConfig[];
}
export interface MappedParameter {
    fieldId: string;
    columnName: string;
  }

export type WorkflowExecutionLogLevel = 10 | 30 | 40;
export type Inputs = Record<string, WorkflowInputConfig>;
export type Constants = Record<string, string | number>;

export interface WorkflowConfig {
    name?: string; // TODO Whyyyy name is also here???
    clientConfig?: ClientConfig;
    hasSpreadSheetInput?: boolean;
    enableLongRunning?: boolean;
    // TODO add typing
    constants: Constants,
    inputs: Inputs,
    workflowTags: Record<string, string>,
    contexts: Record<string, string>,
    apiFieldReferences: Record<string, string>,

    // TODO remove on DM cleanup
    connectionContext?: Record<string, string>,
    // TODO end add typing

    logLevel?: WorkflowExecutionLogLevel;
    notificationEmail?: string;
    notifyOnError?: boolean;
    steps: Step[];

    // TODO should be in step config
    excelDesignFileKey?: string;
    hasExcelDesignFile?: boolean;
    templateFileKey?: string;
    hasTemplateFile?: boolean;

    isControlSpreadSheetEnabled?: boolean;
    isEnforceSheetVerificationEnabled?: boolean;
    saveBackup?: boolean;
    runtimeConfig?: WorkflowRuntimeConfig;
}

export interface WorkflowDefinition {
    workflowConfig: WorkflowConfig
    stepConfig: Record<string, StepDefinition>;
}
export interface WorkflowExecutionKey {
    expression: string;
    name: string;
}

export interface Workflow {
    workflowId?: string;
    stats?: Stats;
    overview: Overview;
    entity: string;
    active: boolean;
    hasError: boolean;
    workflowName: string;
    workflowAlias: string;
    description: string;
    workflowArn?: string;
    tenantId?: string;
    workflowType: WorkflowType;
    documentId?: string | null;
    labels: string[];
    errors?: string[];
    resourceGroup?: string;

    tags: WorkflowTags;
    definition?: WorkflowDefinition;
    createdBy?: string;
    created?: string;
    modified?: string;
    modifiedBy?: string;

    executionKeys?: Record<string, WorkflowExecutionKey>;
}

export interface NewWorkflow { // TODO is this needed at all? or we can use already workflow directly?
    entity: string;
    workflowName: string;
    workflowAlias: string;
    description: string;
    tenantId?: string;
    active: boolean;
    workflowType?: WorkflowType;
    definition: WorkflowDefinition;
    hasError: boolean;
    labels: String[];
    tags: WorkflowTags;
}

export type WorkflowListResponse = APIListResponse<Workflow>

export interface WorkflowListRequest {
    limit?: number;
    search?: string;
    withStepType?: StepType | string;
}

export interface WorkflowExecutionSearchRequest {
    workflowId: string;
    search?: string;
    limit?: number;
    failed?: boolean | null;
}

export interface ExpandedWorkflow extends Workflow {
    filterText?: string;
    isFavorite?: boolean;
    createdByUser?: User | null;
    modifiedByUser?: User | null;
    lastAccessed?: string;
    groups?: { id: string, name: string }[];
    hasActiveSchedule?: boolean;
}

export interface StepWithDefinition {
    step: Step;
    definition?: StepDefinition;
}

export enum FieldType {
    INPUT = 'I',
    OUTPUT = 'O'
}

export enum FieldValueSource {
    CONSTANT = 'CONSTANT',
    CLIENT = 'CLIENT',
    API = 'API',
    EXCEL = 'EXCEL',
    CELL = 'CELL',
    COLUMN = 'COLUMN',
    TEXT = 'TEXT',
    EVENT = 'EVENT',
    CONTEXT = 'CONTEXT',
    TAG = 'TAG',
    EMPTY = '_'
}

// Not to leak internals of each and every step implementation that can share fields with other steps in
// WF model below is created.
export interface ExposedField {
    locator: string; // "Address" within the step for field - for m3 api it will be apiItem.output/input.parameterName
    fieldName: string;
    fieldType: FieldType;
    fieldLabel: string;
    dataType: DataType;
    length?: number;
    overriddenStepType?: StepType | 'MSG'; // TODO Added to support MSG
    mandatory?: boolean;
    hasAssignedValue?: boolean; // true when field has a source assigned in the step config (e.g. TAG, CLIENT, CONSTANT)
    assignedValueSource?: FieldValueSource;
    assignedValue?: string;
}

// m3 api items will be represented as "groups"
export interface FieldsGroup {
    id: string;
    label: string;
    description?: string;
    fields: ExposedField[];
}

export interface StepFields {
    stepId: string;
    stepType: StepType;
    fieldsGroups: FieldsGroup[];
}

export interface MappedField {
    locator: string; // "Address" within the step for field - for m3 api it will be apiItem.output/input.parameterName
    fromStepId?: string;
    fromStepType: StepType | 'MSG' | 'TEXT' | 'CONSTANT';
    label?: string;
    toStepId: string;
    toStepType: StepType;
    toStepValueSource: FieldValueSource; // Naming is bad, but I cannot find any better as of now - after mapping will be rethought should be fixed as well.
    mappedTo: string;
    dataType: DataType;
}

export interface DraggedFields {
    fields: ExposedField[];
    fieldGroupId: string;
    fieldGroupDescription: string;
    stepType: StepType;
    stepId: string;
}

/**
 * To hide exposed fields
 */
export interface ExposedFieldsFilter {
    stepTypes?: StepType[];
    fieldType?: FieldType;
    fieldGroupId?: string;
    noOverrideTypes?: boolean;
    excludedFields?: {
        fieldGroupId: string;
        fieldName: string;
    }[];
}

export interface RunWorkflowProps {
    workflowId: string;
    headerParams?: WorkflowExecutionRequestHeaders;
    controlParams?: ExecutionControlParameters
    body: object
}
